/**
 * TalentGridPersonCard — one person on the talent map.
 *
 * Renders the initials chip and the card that opens from it. The card opens
 * on hover or keyboard focus, stays open while the pointer is over it (a
 * short grace period covers the gap between chip and card), pins open on
 * click or Enter so a keyboard user can read it and use the Move controls,
 * and closes on Escape, on an outside click, or when the pointer leaves.
 *
 * When the chair can move people (`canMove` + `onMove`) the card ends with
 * the same Mastery / Growth / Turn choice as the placement checklist plus a
 * performance choice, and a "Record move" button. Someone still awaiting
 * placement gets the same choice to place them, with "Place on the grid".
 */
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { ArrowRight, Clock, Fingerprint, Slash, Star } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { TalentGridPerson } from '@/services/api/talentReviewService'
import { CampusInputLines } from './CampusInputView'
import {
  BLOCK_TONE_CLASS,
  CHIP_TONE_CLASS,
  MIDYEAR_RATING_LABEL,
  NOT_PLACEABLE_CHIP_CLASS,
  AWAITING_CHIP_CLASS,
  PERFORMANCE_LABEL,
  POTENTIAL_LABEL,
  POTENTIAL_SHORT,
  ZONE_TEXT_CLASS,
  ZONE_TONE,
  blockByNumber,
  eduvosBlock,
  getInitials,
  zoneOf,
  type Level,
  READINESS_LABEL,
  RETENTION_RISK_LABEL,
  RATING_PERIOD_LABEL,
} from './eduvosBlocks'
import RatingTrend from './RatingTrend'
import KpaRatings from './KpaRatings'

/** Pointer grace period between leaving the chip and the card closing. */
const CLOSE_DELAY_MS = 150
/** A pointer has to rest on a chip before its card opens — sweeping across a
 *  cell must not pop a card per chip. */
const OPEN_DELAY_MS = 200

/** Only one card is open at a time: opening a chip closes the previous one. */
let closeActiveCard: (() => void) | null = null

const PERFORMANCE_ORDER: Level[] = ['low', 'medium', 'high']
const POTENTIAL_ORDER: Level[] = ['low', 'medium', 'high']

const PERFORMANCE_SHORT: Record<Level, string> = {
  low: 'Weak / NYF',
  medium: 'Full',
  high: 'Exceptional',
}

const STATUS_LABEL: Record<TalentGridPerson['status'], string> = {
  pending: 'Awaiting placement',
  draft: 'Placement in draft',
  submitted: 'Placement submitted',
  reviewed: 'Reviewed in session',
  published: 'Published',
}

export interface TalentGridPersonCardProps {
  person: TalentGridPerson
  reviewYear: number
  canMove?: boolean
  onMove?: (person: TalentGridPerson, performance: Level, potential: Level) => Promise<void> | void
  /** Record an action or note for this person from the card (the room, in session). */
  onAction?: (person: TalentGridPerson) => void
  /** Open this person's Growth Plan. Offered for everyone on the grid,
      not only blocks 1, 2 and 3 (Victor, 9 Sep 2026). */
  onOpenPlan?: (person: TalentGridPerson) => void
  className?: string
}

/** Chip classes and accessible naming for a person, whatever their state. */
function describeChip(person: TalentGridPerson) {
  const block = blockByNumber(person.block)
  const zone = zoneOf(block)
  if (block && zone) {
    return {
      block,
      zone,
      chipClass: CHIP_TONE_CLASS[ZONE_TONE[zone]],
      ariaLabel: `${person.name}, block ${block.number} ${block.label}`,
    }
  }
  if (person.not_placeable_reason) {
    return {
      block: undefined,
      zone: undefined,
      chipClass: NOT_PLACEABLE_CHIP_CLASS,
      state: 'not-on-grid' as const,
      ariaLabel: `${person.name}, not on the grid this year: ${person.not_placeable_reason}`,
    }
  }
  return {
    block: undefined,
    zone: undefined,
    chipClass: AWAITING_CHIP_CLASS,
    state: 'awaiting' as const,
    ariaLabel: `${person.name}, awaiting placement by their People Leader`,
  }
}

/** The corner mark that says which of the two off-grid states a chip is in.
 *  Bottom-right, so it never collides with the key-person dot above it. */
function ChipStateMark({ state }: { state: 'not-on-grid' | 'awaiting' }) {
  const Icon = state === 'not-on-grid' ? Slash : Clock
  return (
    <span
      aria-hidden
      data-demo={`talent-chip-${state}`}
      className="absolute -bottom-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white ring-1 ring-gray-300"
    >
      <Icon className="h-2.5 w-2.5 text-gray-600" strokeWidth={2.5} />
    </span>
  )
}

function Eyebrow({ children }: { children: ReactNode }) {
  return <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{children}</p>
}

/** A fact row; nothing at all when there is nothing to show. */
function Row({ label, value }: { label: string; value: ReactNode }) {
  if (value === null || value === undefined || value === '') return null
  return (
    <>
      <dt className="text-gray-500">{label}</dt>
      <dd className="min-w-0 text-gray-800">{value}</dd>
    </>
  )
}

export default function TalentGridPersonCard({ person, reviewYear, canMove = false, onMove, onAction, onOpenPlan, className }: TalentGridPersonCardProps) {
  const [open, setOpen] = useState(false)
  // Pinned = opened by click or keyboard; hover leaving does not close it.
  const pinnedRef = useRef(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Set when the card is being closed from the keyboard (Escape, tabbing out)
  // so Radix may hand focus back to the chip; pointer closes never move focus.
  const keyboardCloseRef = useRef(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const { block, zone, chipClass, ariaLabel, state } = describeChip(person)
  const initials = person.initials || getInitials(person.name)

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const cancelOpen = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current)
      openTimer.current = null
    }
  }, [])

  const closeNow = useCallback(() => {
    cancelOpen()
    cancelClose()
    pinnedRef.current = false
    setOpen(false)
    if (closeActiveCard === closeNowRef.current) closeActiveCard = null
  }, [cancelClose, cancelOpen])
  const closeNowRef = useRef(closeNow)
  closeNowRef.current = closeNow

  /** Make this the one open card. */
  const claimActive = useCallback(() => {
    if (closeActiveCard && closeActiveCard !== closeNowRef.current) closeActiveCard()
    closeActiveCard = closeNowRef.current
  }, [])

  const scheduleClose = useCallback(() => {
    cancelOpen()
    if (pinnedRef.current) return
    cancelClose()
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null
      if (!pinnedRef.current) closeNowRef.current()
    }, CLOSE_DELAY_MS)
  }, [cancelClose, cancelOpen])

  const openFromHover = useCallback(() => {
    cancelClose()
    if (open || openTimer.current) return
    openTimer.current = setTimeout(() => {
      openTimer.current = null
      claimActive()
      setOpen(true)
    }, OPEN_DELAY_MS)
  }, [cancelClose, claimActive, open])

  const openPinned = useCallback(() => {
    cancelOpen()
    cancelClose()
    claimActive()
    pinnedRef.current = true
    setOpen(true)
  }, [cancelClose, cancelOpen, claimActive])

  useEffect(
    () => () => {
      cancelClose()
      cancelOpen()
      if (closeActiveCard === closeNowRef.current) closeActiveCard = null
    },
    [cancelClose, cancelOpen],
  )

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    // Radix would toggle on this click; we decide ourselves so a click pins
    // an already hover-opened card instead of closing it.
    e.preventDefault()
    if (pinnedRef.current) closeNow()
    else openPinned()
  }

  const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      openPinned()
      // Content mounts on the next paint; move focus into it then.
      requestAnimationFrame(() => contentRef.current?.focus())
    }
  }

  const handleBlur = () => {
    // Focus moving into the card keeps it open; anything else lets it close.
    requestAnimationFrame(() => {
      if (contentRef.current?.contains(document.activeElement)) return
      if (!pinnedRef.current) scheduleClose()
    })
  }

  // --- Move / Place ---------------------------------------------------------
  // A move needs a placement to move: the People Leader's submitted one. Someone
  // still awaiting placement once the room is open joined the list after it was
  // handed in, or after the session started, and nobody else can place them now:
  // the room places them here (Victor, 6 Oct 2026).
  // Impact starts where their mid-year rating suggests, as their list would have.
  const moveEnabled = canMove && !!onMove && person.block != null
  const placeEnabled = canMove && !!onMove && person.block == null && !person.not_placeable_reason
  const startPerformance: Level | undefined =
    (person.performance ?? (placeEnabled ? person.performance_default : null)) ?? undefined
  const [draftPerformance, setDraftPerformance] = useState<Level | undefined>(startPerformance)
  const [draftPotential, setDraftPotential] = useState<Level | undefined>(person.potential ?? undefined)
  const [moving, setMoving] = useState(false)
  const [moveError, setMoveError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setDraftPerformance(startPerformance)
      setDraftPotential(person.potential ?? undefined)
      setMoveError(null)
    }
  }, [open, startPerformance, person.potential])

  const targetBlock = eduvosBlock(draftPerformance, draftPotential)
  const unchanged = draftPerformance === (person.performance ?? undefined) && draftPotential === (person.potential ?? undefined)

  const recordMove = async () => {
    if (!onMove || !draftPerformance || !draftPotential) return
    setMoving(true)
    setMoveError(null)
    try {
      await onMove(person, draftPerformance, draftPotential)
      closeNow()
    } catch (err) {
      setMoveError((err as Error)?.message || (placeEnabled ? 'The placement could not be recorded.' : 'The move could not be recorded.'))
    } finally {
      setMoving(false)
    }
  }

  const changedFrom = person.position_changed && person.changed_from
    ? eduvosBlock(person.changed_from.performance, person.changed_from.potential)
    : undefined

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        // Radix reports Escape and outside interactions here.
        if (next) openPinned()
        else closeNow()
      }}
    >
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          data-demo="talent-grid-person"
          data-employee-id={person.employee_id}
          aria-label={ariaLabel}
          onClick={handleClick}
          onMouseEnter={openFromHover}
          onMouseLeave={scheduleClose}
          onFocus={openFromHover}
          onBlur={handleBlur}
          onKeyDown={handleTriggerKeyDown}
          className={cn(
            'relative inline-flex h-7 w-7 shrink-0 select-none items-center justify-center rounded-full text-[11px] font-semibold leading-none',
            'outline outline-2 outline-white transition-transform hover:scale-110',
            'focus-visible:ring-2 focus-visible:ring-heritage-blue focus-visible:ring-offset-2',
            'data-[state=open]:ring-2 data-[state=open]:ring-heritage-blue data-[state=open]:ring-offset-2',
            chipClass,
            className,
          )}
        >
          {initials}
          {state && <ChipStateMark state={state} />}
          {person.key_person && (
            <span
              aria-hidden
              className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-amber-400 ring-1 ring-white"
            />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        ref={contentRef}
        data-demo="talent-grid-card"
        side="bottom"
        align="start"
        sideOffset={8}
        // Never taller than the room Radix measures between the chip and the
        // viewport edge (the --radix-popover-content-available-height it sets),
        // flipping above the chip when there is more room there; the body
        // scrolls inside. (Victor, 8 Sep 2026: the card ran off the screen.)
        collisionPadding={16}
        avoidCollisions
        sticky="always"
        tabIndex={-1}
        aria-label={`${person.name}: placement details`}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => {
          // Only a keyboard close hands focus back to the chip.
          if (!keyboardCloseRef.current) e.preventDefault()
          keyboardCloseRef.current = false
        }}
        onFocusOutside={() => {
          keyboardCloseRef.current = true
        }}
        onInteractOutside={(e) => {
          const type = (e.detail.originalEvent as Event | undefined)?.type || ''
          if (type.startsWith('pointer') || type.startsWith('mouse') || type.startsWith('touch')) keyboardCloseRef.current = false
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault()
          keyboardCloseRef.current = !!contentRef.current?.contains(document.activeElement)
          closeNow()
        }}
        className="z-[400] flex max-h-[min(var(--radix-popover-content-available-height),40rem)] w-[22rem] max-w-[calc(100vw-1.5rem)] flex-col overflow-x-hidden overflow-y-auto rounded-xl border p-0 text-sm shadow-lg"
      >
        {/* Header (stays put while the rest scrolls) */}
        <div className="sticky top-0 z-10 border-b bg-white px-4 py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate font-primary font-semibold leading-tight text-heritage-blue">{person.name}</p>
              <p className="truncate text-xs text-gray-500">{person.job_title || 'No title on record'}</p>
            </div>
            {block && zone ? (
              <Badge className={cn('shrink-0 border', BLOCK_TONE_CLASS[ZONE_TONE[zone]])}>Block {block.number}</Badge>
            ) : (
              <Badge variant="outline" className="shrink-0 border-dashed border-gray-500 text-gray-600">
                {person.not_placeable_reason ? 'Not placeable' : 'Not placed'}
              </Badge>
            )}
          </div>
          {block && zone && (
            <p className={cn('mt-1.5 text-xs font-medium', ZONE_TEXT_CLASS[zone])}>{block.label}</p>
          )}
          {!block && person.not_placeable_reason && (
            <p className="mt-1.5 text-xs text-gray-600">{person.not_placeable_reason}</p>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500">
            <span>{STATUS_LABEL[person.status]}</span>
            {changedFrom && (
              <span className="inline-flex items-center gap-1">
                <ArrowRight className="h-3 w-3" /> moved from block {changedFrom.number}
              </span>
            )}
          </div>
        </div>

        {/* Facts */}
        <dl className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 px-4 py-3 text-xs">
          <Row label="Function" value={person.function} />
          <Row label="Department" value={person.department} />
          <Row label="Campus" value={person.campus} />
          <Row label="People Leader" value={person.leader_name} />
          <Row label="One-Up" value={person.one_up_name} />
          <Row label="Tenure" value={person.tenure_years != null ? `${person.tenure_years.toFixed(1)} yrs` : null} />
          <Row
            label="In role"
            value={
              person.role_tenure_years != null
                ? `${person.role_tenure_years.toFixed(1)} yrs${person.current_role_since ? ` · since ${person.current_role_since.slice(0, 10)}` : ''}`
                : null
            }
          />
          {person.previous_roles && person.previous_roles.length > 0 && (
            <Row
              label="Previous roles"
              value={
                <ul className="space-y-0.5">
                  {person.previous_roles.slice(0, 4).map((r, i) => (
                    <li key={`${r.title}-${i}`}>
                      {r.title}
                      {r.from || r.to ? (
                        <span className="text-gray-500">
                          {' '}· {r.from ? r.from.slice(0, 4) : '?'}–{r.to ? r.to.slice(0, 4) : '?'}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              }
            />
          )}
          <Row label="Grade" value={person.grade} />
          <Row label="Contract" value={person.employment_category || person.position_type} />
          <Row
            label="Qualification"
            value={person.highest_qualification ? `${person.highest_qualification}${person.highest_nqf ? ` · NQF ${person.highest_nqf}` : ''}` : null}
          />
          <Row label="Nationality" value={person.nationality} />
          <Row
            label="Mid-year rating"
            value={person.midyear_rating ? MIDYEAR_RATING_LABEL[person.midyear_rating] || person.midyear_rating : null}
          />
          {person.performance && person.performance_default && person.performance !== person.performance_default && (
            <Row
              label="Impact judged"
              value={`${PERFORMANCE_SHORT[person.performance]}, where the mid-year suggested ${PERFORMANCE_SHORT[person.performance_default]}${person.impact_reason ? `: ${person.impact_reason}` : ''}`}
            />
          )}
          {person.performance && (!person.midyear_rating || person.midyear_rating === 'tstr') && (
            <Row label="Impact judged" value={`${PERFORMANCE_SHORT[person.performance]}, with no mid-year rating to go on`} />
          )}
          <Row label="Potential" value={person.potential ? POTENTIAL_SHORT[person.potential] : null} />

          {person.key_person && (
            <Row
              label="Key person"
              value={
                <span className="inline-flex items-start gap-1">
                  <Star className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                  <span>{person.key_person_reason || 'Yes'}</span>
                </span>
              }
            />
          )}
          {person.is_hipo && block && (
            <Row label="High-potential" value={`Yes — block ${block.number}`} />
          )}
          {person.next_role && <Row label="Next role" value={person.next_role} />}
          {person.readiness && <Row label="Readiness" value={READINESS_LABEL[person.readiness] || person.readiness} />}
          {person.key_person && person.retention_risk && (
            <Row label="Retention risk" value={RETENTION_RISK_LABEL[person.retention_risk] || person.retention_risk} />
          )}
        </dl>

        {/* Rationale */}
        {person.rationale && (
          <div className="border-t px-4 py-3">
            <Eyebrow>Rationale</Eyebrow>
            <p className="mt-1 whitespace-pre-line text-xs text-gray-700">{person.rationale}</p>
          </div>
        )}

        {/* What the campus General Manager sees — given outside the session
            entirely (a campus GM and the HR process owner, 8 Sep 2026): the room reads it beside the
            People Leader's own words. */}
        {(person.campus_inputs || []).length > 0 && (
          <div className="border-t px-4 py-3" data-demo="talent-card-campus-input">
            <Eyebrow>From the campus</Eyebrow>
            <div className="mt-1 space-y-2">
              {(person.campus_inputs || []).map((i) => (
                <CampusInputLines key={i.id} input={i} />
              ))}
            </div>
          </div>
        )}

        {/* Mid-year KPAs — the four ratings behind the mid-year overall */}
        {person.midyear_kpas && person.midyear_kpas.length > 0 && (
          <div className="border-t px-4 py-3">
            <Eyebrow>Mid-year KPAs</Eyebrow>
            <KpaRatings kpas={person.midyear_kpas} className="mt-1" />
          </div>
        )}

        {/* Impact history — the formal ratings on record, oldest first */}
        {person.rating_history && person.rating_history.length > 0 && (
          <div className="border-t px-4 py-3">
            <Eyebrow>Impact history</Eyebrow>
            {person.rating_history.length >= 2 && (
              <div className="mt-1">
                <RatingTrend history={person.rating_history} height={52} />
                <p className="text-[10px] text-gray-400">Filled: platform ratings. Hollow: F&O, before the platform.</p>
              </div>
            )}
            <ul className="mt-1 space-y-0.5 text-xs text-gray-700">
              {person.rating_history.map((r) => (
                <li key={`${r.review_year}-${r.review_type}`} className="flex justify-between gap-3">
                  <span className="text-gray-500">
                    {r.review_year} {RATING_PERIOD_LABEL[r.review_type] || r.review_type}
                  </span>
                  <span>
                    {MIDYEAR_RATING_LABEL[r.rating] || r.label || r.rating}
                    {r.source === 'fo' ? ' · F&O' : r.calibrated ? '' : ' · not calibrated'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Growth Plan */}
        <div className="border-t px-4 py-3">
          <Eyebrow>Growth Plan</Eyebrow>
          {(person.growth_plan_gaps || []).length > 0 ? (
            <p className="mt-1 text-xs text-amber-800">Incomplete: {person.growth_plan_gaps.join(', ')}</p>
          ) : person.idp ? (
            <p className="mt-1 text-xs text-emerald-800">Complete</p>
          ) : null}
          {person.idp ? (
            <div className="mt-1 space-y-1.5 text-xs text-gray-700">
              {person.idp.focus && <p className="font-medium text-gray-800">{person.idp.focus}</p>}
              {person.idp.areas.length > 0 && (
                <div>
                  <p className="text-gray-500">Growth opportunities</p>
                  <ul className="list-disc space-y-0.5 pl-4">
                    {person.idp.areas.map((a, i) => (
                      <li key={`${a}-${i}`}>{a}</li>
                    ))}
                  </ul>
                </div>
              )}
              {person.idp.interventions.length > 0 && (
                <div>
                  <p className="text-gray-500">Growth enablements</p>
                  <ul className="list-disc space-y-0.5 pl-4">
                    {person.idp.interventions.map((a, i) => (
                      <li key={`${a}-${i}`}>{a}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(person.idp.target_role || person.idp.future_roles.length > 0 || person.idp.mobility) && (
                <dl className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 pt-1">
                  {person.idp.target_role && (
                    <>
                      <dt className="text-gray-500">Target role</dt>
                      <dd>{person.idp.target_role}</dd>
                    </>
                  )}
                  {person.idp.future_roles.length > 0 && (
                    <>
                      <dt className="text-gray-500">Future roles</dt>
                      <dd>{person.idp.future_roles.join(', ')}</dd>
                    </>
                  )}
                  {person.idp.mobility && (
                    <>
                      <dt className="text-gray-500">Geographic mobility</dt>
                      <dd>
                        {person.idp.mobility === 'yes'
                          ? `Open · ${person.idp.preferred_campuses.length > 0 ? person.idp.preferred_campuses.join(', ') : 'no specific campuses noted'}`
                          : person.idp.mobility === 'maybe'
                            ? 'Open in future'
                            : 'Not at this time'}
                      </dd>
                    </>
                  )}
                </dl>
              )}
              {!person.idp.focus && person.idp.areas.length === 0 && person.idp.interventions.length === 0 && (
                <p className="text-gray-500">A plan exists for {reviewYear} but has no growth opportunities yet.</p>
              )}
            </div>
          ) : (
            <p className="mt-1 text-xs text-gray-500">No Growth Plan for {reviewYear}</p>
          )}
          {onOpenPlan && (
            <Button
              size="sm"
              variant="outline"
              className="mt-2 h-7 w-full text-xs"
              onClick={() => { onOpenPlan(person); closeNow() }}
              data-demo="talent-grid-card-plan"
            >
              <Fingerprint className="mr-1 h-3.5 w-3.5" />
              {person.idp ? 'Open their Growth Plan' : 'Start their Growth Plan'}
            </Button>
          )}
        </div>

        {/* An action from the card, so a move and an action happen in one
            place (HR process owner, 8 Sep 2026). */}
        {onAction && (
          <div className="flex items-center justify-between gap-2 border-t px-4 py-2.5">
            <p className="text-[11px] text-gray-600">Something the room agrees to do for {person.name.split(' ')[0]}?</p>
            <Button size="sm" variant="outline" className="h-7 shrink-0 text-xs" onClick={() => { onAction(person); closeNow() }} data-demo="talent-grid-card-action">
              Add action
            </Button>
          </div>
        )}

        {/* Move, or place someone still awaiting placement */}
        {(moveEnabled || placeEnabled) && (
          <div className="space-y-2 border-t bg-gray-50/60 px-4 py-3" data-demo={placeEnabled ? 'talent-grid-card-place' : 'talent-grid-card-move'}>
            <Eyebrow>{placeEnabled ? 'Place' : 'Move'}</Eyebrow>
            {placeEnabled && (
              <p className="text-[11px] text-gray-600">
                {person.leader_name || 'Their People Leader'} has no placement handed in for {person.name.split(' ')[0]}. Place them
                here, and the room can move them like everyone else.
              </p>
            )}
            <div>
              <p className="mb-1 text-[11px] text-gray-500">Performance</p>
              <div className="inline-flex overflow-hidden rounded-md border" role="radiogroup" aria-label={`Performance for ${person.name}`}>
                {PERFORMANCE_ORDER.map((lvl) => (
                  <button
                    key={lvl}
                    type="button"
                    role="radio"
                    aria-checked={draftPerformance === lvl}
                    aria-label={PERFORMANCE_LABEL[lvl]}
                    disabled={moving}
                    onClick={() => setDraftPerformance(lvl)}
                    className={cn(
                      'border-r px-3 py-1.5 text-xs font-medium transition-colors last:border-r-0',
                      draftPerformance === lvl ? 'bg-heritage-blue text-white' : 'bg-background hover:bg-muted',
                    )}
                  >
                    {PERFORMANCE_SHORT[lvl]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1 text-[11px] text-gray-500">Potential</p>
              <div className="inline-flex overflow-hidden rounded-md border" role="radiogroup" aria-label={`Potential for ${person.name}`}>
                {POTENTIAL_ORDER.map((lvl) => (
                  <button
                    key={lvl}
                    type="button"
                    role="radio"
                    aria-checked={draftPotential === lvl}
                    aria-label={POTENTIAL_LABEL[lvl]}
                    disabled={moving}
                    onClick={() => setDraftPotential(lvl)}
                    className={cn(
                      'border-r px-3 py-1.5 text-xs font-medium transition-colors last:border-r-0',
                      draftPotential === lvl ? 'bg-heritage-blue text-white' : 'bg-background hover:bg-muted',
                    )}
                  >
                    {POTENTIAL_SHORT[lvl]}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              <p className="text-[11px] text-gray-600">
                {targetBlock ? (
                  <>
                    Block {targetBlock.number} · {targetBlock.label}
                  </>
                ) : (
                  'Choose performance and potential'
                )}
              </p>
              <Button
                size="sm"
                disabled={moving || !targetBlock || (moveEnabled && unchanged)}
                onClick={recordMove}
                data-demo={placeEnabled ? 'talent-grid-place' : 'talent-grid-record-move'}
              >
                {placeEnabled ? (moving ? 'Placing…' : 'Place on the grid') : moving ? 'Recording…' : 'Record move'}
              </Button>
            </div>
            {moveError && <p className="text-[11px] text-red-700">{moveError}</p>}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
